import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { AddFileComponent } from '../../../shared-components/controls/add-file/add-file.component';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { SelectedSite } from '../../../shared-components/controls/selected-sites-dialog/selected-site';
import { SelectedSitesDialogComponent } from '../../../shared-components/controls/selected-sites-dialog/selected-sites-dialog.component';
import { System } from '../../../shared-components/System';
import { User } from '../../../shared-components/user/user.model';
import { UserService } from '../../../shared-components/user/user.service';
import { LoginService } from '../../../shared-modules/login/login.service';
import { DataQueryInformation } from '../models//data-query-information';
import { DataQuery } from '../models//data-query.model';
import { DataQueryPermissions } from '../models/data-query-permissions';
import { WarningDialogComponent } from '../warning-dialog/warning-dialog.component';
import { TemplatesFromClassicDialogComponent } from './templates-from-classic-dialog/templates-from-classic-dialog.component';

@Component({
    selector: 'add-edit-query-dialog',
    templateUrl: './add-edit-query.component.html',
    styleUrls: ['./add-edit-query.component.css']
})
export class AddEditQueryDialogComponent implements OnInit, OnDestroy {

    // Injected Properties
    headerText = '';
    forEdit = false;
    forImport = false;
    query = new DataQuery();
    dataQueryService: any;	// This is an instance of data query service and must be any to prevent circular dependency issues

    // Properties
    importOption = 0;
    permissions: DataQueryPermissions;
    scopeOptions = ['Private', 'Selected Sites', 'District'];
    scopeLabels = ['Private', null, 'Available to District'];
    scopeIds = ['scopeRadio0', 'scopeRadio1', 'scopeRadio2'];
    scopeDisabledOptions = [];
    fileName: string;
    fileType: string;
    templateName: string;
    errors: ErrorList[] = [];
    subscriptions: Subscription[] = [];
    currentQuery: DataQuery;
    originalQuery: DataQuery;
    information: DataQueryInformation;
    currentUser: User;
    scopeObject: string;

    // Template Properties
    @ViewChild('nameInput', { read: ElementRef, static: true }) nameInput: ElementRef;
    @ViewChild('selectedSitesLink', { read: ElementRef }) selectedSitesLink: ElementRef;
    @ViewChild('templateSelectButton', { read: ElementRef }) templateSelectButton: ElementRef;
    @ViewChild('addFileComponent', { read: AddFileComponent }) addFileComponent: AddFileComponent;

    // Class Functions
    constructor(
        private dialogBox: MatDialogRef<AddEditQueryDialogComponent>,
        private dialog: MatDialog,
        private loginService: LoginService,
        private userService: UserService,
        @Inject(MAT_DIALOG_DATA) private data: any
    ) { }

    // Event Functions
    ngOnInit() {
        // POST: initializes the component
        this.currentUser = this.userService.currentUser;
        // Getting injected properties
        this.headerText = this.data.headerText;
        this.forEdit = this.data.forEdit;
        this.forImport = this.data.forImport;
        this.dataQueryService = this.data.dataQueryService;
        if (this.data.query && this.data.query instanceof DataQuery) {
            if (this.forEdit == true && this.forImport == false) {
                this.query = _.cloneDeep(this.data.query);
            }
            else {
                this.query = this.data.query;
            }

            this.scopeObject = this.query.Scope;
        }
        // Setting up which options are disabled
        this.subscriptions.push(
            this.dataQueryService.getPermissions().subscribe(permissions => {
                this.permissions = permissions;

                // Determining what scopes are available
                this.scopeDisabledOptions = [];
                if (!this.permissions.canUserCreatePrivateQuery) {
                    this.scopeDisabledOptions.push('Private');
                }
                if (!this.permissions.canUserCreateSchoolQuery) {
                    this.scopeDisabledOptions.push('Selected Sites');
                }
                if (!this.permissions.canUserCreateDistrictQuery) {
                    this.scopeDisabledOptions.push('District');
                }

                // Setting the scope of the query
                if (this.permissions.canUserCreatePrivateQuery) {
                    this.query.Scope = 'Private';
                } else if (this.permissions.canUserCreateSchoolQuery) {
                    this.query.Scope = 'Selected Sites';
                } else {
                    this.query.Scope = 'District';
                }
            }),

            // Syncing properties
            this.dataQueryService.observableCurrentQueryStream$.subscribe(currentQuery => {
                this.currentQuery = currentQuery;
            }),
            this.dataQueryService.observableInformationStream$.subscribe(information => {
                this.information = information;
            })
        );

        // Setting query default variables
        if (!this.forEdit) {
            this.query.SavedFromID = this.query.ID;
            this.query.ID = -1;
            this.query.OwnerID = this.userService.currentUser.Id;
            this.query.SelectedSites = '';
            this.query.Deleted = false;
        }

        // Setting the original query for cancelling
        this.originalQuery = new DataQuery();
        Object.assign(this.originalQuery, this.query);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    cancel() {
        // PRE: the cancel button is clicked
        // POST: resets the query and closes the dialog box
        Object.assign(this.query, this.originalQuery);
        this.dialogBox.close();
    }

    import() {
        // PRE: the import button is clicked
        // POST: validates and, if valid, tries to save.

        // Checking for errors
        this.validate();
        if (this.errors.length == 0) {

            // File Importing
            if (this.importOption == 0) {

                // Parsing out export settings
                if (this.query.QueryXML.indexOf('<ExportSettings>') > -1) {
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(this.query.QueryXML, 'text/xml');
                    const exportType = xmlDoc.getElementsByTagName('ExportType')[0].innerHTML;
                    const delimeter = xmlDoc.getElementsByTagName('Delimeter')[0].innerHTML;
                    const otherDelimeter = xmlDoc.getElementsByTagName('OtherDelimeter')[0].innerHTML;
                    const includeColumnHeadings = xmlDoc.getElementsByTagName('IncludeColumnHeadings')[0].innerHTML;
                    this.query.ExportType = exportType;
                    this.query.Delimiter = delimeter;
                    this.query.OtherDelimiter = otherDelimeter;
                    this.query.IncludeColumnHeadings = includeColumnHeadings == 'true';
                }

                // Loading the data from the query
                this.dataQueryService.importing = true;
                this.dataQueryService.importQueryClick = true;
                this.dataQueryService.loadDataFromXML(this.query, this.dataQueryService.importQueryClick);

                // Validating the query manually to load the data on a failure
                this.query.queryChangedSinceLastResult = false;	// We do this to prevent results tab from validating, too
                this.dataQueryService.validateQuery(
                    () => {

                        // Saving the query with loaded data
                        this.dataQueryService.saveQuery(
                            this.query,
                            results => {

                                // If successful, trim the query XML and set it to the current query
                                if (results[0] == 'Success') {
                                    this.query.queryChangedSinceLastResult = true;
                                    this.dataQueryService.currentQuery = this.query;
                                    this.query.QueryXML = this.query.QueryXML.substring(0, this.query.QueryXML.indexOf('</root>') + 7);
                                    this.dialogBox.close();
                                }

                                // Otherwise, show warning circle
                                else {
                                    this.errors.push({
                                        fieldName: null,
                                        fieldRef: this.nameInput,
                                        errorMsg: 'A query with this name already exists.'
                                    });
                                }
                                this.dataQueryService.importing = false;
                            },
                            results => {
                                if (results[0] != 'Success') {
                                    this.errors.push({
                                        fieldRef: this.nameInput,
                                        fieldName: 'Query Name',
                                        errorMsg: results[0]
                                    });
                                }
                                this.dataQueryService.importing = false;
                            },
                            false		// We don't include validation because we want the user to be able to fix it after importing
                        );
                    },
                    () => {

                        // Moving to Settings for Export to fix problems
                        this.dataQueryService.currentTab = 'Settings for Export';

                        // We need to wait for the tab change to occur so we don't get multiple errors from results tab
                        setTimeout(() => {

                            // Setting this as the current query to fix the errors
                            this.query.queryChangedSinceLastResult = true;
                            this.dataQueryService.currentQuery = this.query;

                            // Closing the dialog
                            this.dialogBox.close();
                            this.dataQueryService.importing = false;
                        }, 100);
                    },
                    true,
                    this.query
                );
            }

            // Classic Importing
            else {
                this.subscriptions.push(
                    this.dataQueryService.importQueryFromLegacy(this.query).subscribe(importResults => {

                        // Loading the imported query data
                        this.query = System.convert<DataQuery>(importResults, new DataQuery());
                        this.dataQueryService.loadDataFromXML(this.query);

                        // Saving the query with loaded data
                        this.dataQueryService.saveQuery(
                            this.query,
                            (saveResults) => {

                                // If successful, set this query as the current query
                                if (saveResults[0] == 'Success') {
                                    this.query.queryChangedSinceLastResult = true;
                                    this.dataQueryService.loadQuery(this.query);
                                    this.dataQueryService.currentQuery = this.query;
                                    this.dialogBox.close(this.query);
                                }

                                // If unsuccessful, show warnings
                                else {

                                    // Show warning dialog in V10 with certain permissions
                                    if (!this.loginService.LoginSettings.V10
                                        && saveResults && saveResults.length >= 2
                                        && ((this.query.Scope == 'Private' && this.query.OwnerID == this.userService.currentUser.Id)
                                            || (this.query.Scope == 'Selected Sites' && this.permissions.canUserModifySchoolQuery)
                                            || (this.query.Scope == 'District' && this.permissions.canUserModifyDistrictQuery)
                                        )
                                    ) {
                                        const dialogBox = this.dialog.open(WarningDialogComponent, {
                                            disableClose: true,
                                            data: {
                                                warningMessage: 'A query with this name already exists.',
                                                allowOverwrite: true,
                                                query: this.query,
                                                existingQueryId: saveResults[1],
                                                dataQueryService: this.dataQueryService
                                            }
                                        });
                                        this.subscriptions.push(
                                            dialogBox.afterClosed().subscribe(() => {
                                                this.dialogBox.close();
                                            })
                                        );
                                    }

                                    // Otherwise, show error circle
                                    else {
                                        this.errors.push({
                                            fieldName: null,
                                            fieldRef: this.nameInput,
                                            errorMsg: 'A query with this name already exists.'
                                        });
                                    }
                                }
                                this.dataQueryService.importing = false;
                            },
                            () => {
                                this.dataQueryService.importing = false;
                            },
                            false		// We don't include validation because it's guaranteed to be valid from classic
                        );
                    })
                );
            }
        }
    }

    save() {
        // PRE: the save button is clicked
        // POST: validates and, if valid, saves the query then closes the dialog box

        // We must load the query before we save it
        if (!this.query.xmlLoaded) {
            this.dataQueryService.loadDataFromXML(this.query);
        }

        // Helper Function
        const serverCall = () => {
            this.dataQueryService.saveQuery(
                this.query,
                (result) => {
                    if (result && result[0] != 'A query with the same name already exists.') {
                        this.dialogBox.close(this.query);
                    } else {
                        this.errors.push({
                            fieldName: null,
                            fieldRef: this.nameInput,
                            errorMsg: 'A query with this name already exists.'
                        });
                    }
                }
            );
        };

        // Validation
        this.validate();
        if (this.errors.length == 0) {

            // If we're not editing, we have to add the report template
            if (!this.forEdit) {
                this.subscriptions.push(
                    this.dataQueryService.getReportTemplateXML(this.currentQuery.ID).subscribe(reportXML => {
                        this.query.QueryXML += reportXML;
                        serverCall();
                    })
                );
            }

            // Otherwise, just save
            else {
                serverCall();
            }
        }
    }

    setFile(XML: string) {
        // PRE: a file has been added
        // POST: sets up the imported query's XML  based on the file
        this.query.QueryXML = XML;
    }

    importQuerySelect() {
        // PRE: the select button under the import query option is clicked
        // POST: opens the dialog to select a legacy query
        this.dataQueryService.getLegacyQueries().subscribe(legacyQueries => {
            const legacyDialogBox = this.dialog.open(TemplatesFromClassicDialogComponent, {
                disableClose: true,
                width: '80vw',
                height: '80vh',
                data: {
                    legacyQueries: legacyQueries
                }
            });
            legacyDialogBox.afterClosed().subscribe(legacyQuery => {
                if (legacyQuery) {
                    this.templateName = legacyQuery.QueryName;
                    this.query = legacyQuery;
                }
            });
        });
    }

    selectedSitesClick() {
        // PRE: the selected sites link is clicked
        // POST: opens the selected sites
        const selectedSites: SelectedSite[] = [];
        this.query.SelectedSites.split(',').forEach(siteIdString => {
            const siteId = Number(siteIdString.trim());
            if (!isNaN(siteId)) {
                const ss = new SelectedSite();
                ss.Id = siteId;
                selectedSites.push(ss);
            }
        });
        const selectedSitesDialogBox = this.dialog.open(SelectedSitesDialogComponent, {
            disableClose: true,
            width: '700px',
            data: {
                selectedSites: selectedSites
            }
        });
        selectedSitesDialogBox.afterClosed().subscribe((results: SelectedSite[]) => {
            if (results) {
                this.query.SelectedSites = results.map(o => o.Id).join(', ');
                this.query.SelectedSiteNames = results.map(o => o.SchoolName).join(', ');
            }
        });
    }

    // Methods
    validate() {
        // POST: sets up the errors
        this.errors = [];

        // File name is required if our option is 0
        if (this.forImport && this.importOption == 0 && !this.fileName) {
            const elementRef = this.addFileComponent ? this.addFileComponent.browseButton : null;
            this.errors.push({
                fieldName: 'File Name',
                fieldRef: elementRef,
                errorMsg: 'Field is required.'
            });
        }

        // File type Only XML is required if our option is 0
        if (this.forImport && this.importOption == 0 && this.addFileComponent && this.addFileComponent.fileType != undefined && this.addFileComponent.fileType != 'text/xml' && this.addFileComponent.fileType != 'application/xml') {
            const elementRef = this.addFileComponent ? this.addFileComponent.deleteButton : null;
            this.errors.push({
                fieldName: 'File',
                fieldRef: elementRef,
                errorMsg: 'Invalid type. File must be one created from export of existing query.'
            });
        }

        // Template name is required if our option is 1
        if (this.forImport && this.importOption == 1 && !this.templateName) {
            this.errors.push({
                fieldName: 'Classic Application Query',
                fieldRef: this.templateSelectButton,
                errorMsg: 'Field is required.'
            });
        }

        // Sites is required if this is selected site scope
        if (this.query.Scope == 'Selected Sites' && !this.query.SelectedSites) {
            this.errors.push({
                fieldName: 'Selected Sites',
                fieldRef: this.selectedSitesLink,
                errorMsg: 'No Sites selected.'
            });
        }

        // Query name is required
        if (!this.query.QueryName) {
            this.errors.push({
                fieldName: 'Query Name',
                fieldRef: this.nameInput,
                errorMsg: 'Field is required'
            });
        }
    }
    viewScopeChange() {
        this.query.Scope = this.scopeObject;
    }

    takeOwnershipClick() {
        this.query.OwnerID = this.currentUser.Id;
        this.query.OwnerName = this.currentUser.LastName + ', ' + this.currentUser.FirstName;
    }
}
